import React from 'react';
import { Badge, Button } from 'react-bootstrap';
import { Workflow } from '../types';
import './WorkflowVisualizerHorizontal.css';

interface WorkflowVisualizerHorizontalProps {
  workflow: Workflow | null;
  currentStep?: string;
  ticketStatus?: string;
  displayStepOrder?: string[];
  nextStepCount?: number;
  onAdvance?: () => void;
  onGoBack?: () => void;
  onConclude?: () => void;
  canAdvance?: boolean;
  canGoBack?: boolean;
  canConclude?: boolean;
  showExtraAction?: boolean;
  extraActionLabel?: string;
  onExtraAction?: () => void;
  isExtraActionLoading?: boolean;
  isLoading?: boolean;
  isGoingBack?: boolean;
  isConcluding?: boolean;
  isLastStep?: boolean;
}

const WorkflowVisualizerHorizontal: React.FC<WorkflowVisualizerHorizontalProps> = ({ 
  workflow, 
  currentStep,
  ticketStatus,
  displayStepOrder = [],
  nextStepCount = 0,
  onAdvance,
  onGoBack,
  onConclude,
  canAdvance = false,
  canGoBack = false,
  canConclude = false,
  showExtraAction = false,
  extraActionLabel = '',
  onExtraAction,
  isExtraActionLoading = false,
  isLoading = false,
  isGoingBack = false,
  isConcluding = false,
  isLastStep: isLastStepProp = false
}) => {
  if (!workflow || !workflow.steps) {
    return (
      <div className="workflow-horizontal">
        <p className="text-muted mb-0">Nenhum fluxo configurado</p>
      </div>
    );
  }

  let steps: any[] = [];
  try {
    const stepsData = JSON.parse(workflow.steps);
    if (Array.isArray(stepsData)) {
      steps = stepsData;
    } else if (stepsData.steps && Array.isArray(stepsData.steps)) {
      steps = stepsData.steps;
    }
  } catch (e) {
    steps = workflow.steps.split(',').map((s: string) => ({ name: s.trim(), status: 'em_andamento' }));
  }

  const getStepName = (step: any): string => (typeof step === 'string' ? step : (step.name || step));
  const normalizeStepName = (stepName?: string): string => String(stepName || '').trim().toLowerCase();

  const originalSteps = steps.map((step, index) => ({
    step,
    index,
    name: getStepName(step),
  }));

  const hasBranchingPaths = steps.some((step: any) => {
    if (!step || typeof step !== 'object') return false;

    const previousSteps = step.previous_steps;
    if (Array.isArray(previousSteps) && previousSteps.length > 0) return true;

    const options = step.options;
    if (Array.isArray(options) && options.length > 0) return true;

    const next = step.next;
    if (Array.isArray(next) && next.length > 1) return true;

    // Em compras, etapas com acesso livre funcionam como caminhos alternativos.
    if (step.allow_anytime_access === true) return true;

    return false;
  });

  // A ordem visual deve seguir sempre a configuração oficial do workflow.
  // O histórico do ticket (displayStepOrder) é usado apenas para estado (concluído/pendente),
  // evitando que passos antigos "voltem para frente" após avançar/retroceder.
  const orderedSteps = [...originalSteps];
  const isTicketClosed = ticketStatus?.toLowerCase() === 'fechado' || ticketStatus?.toLowerCase() === 'cancelado';
  const shouldUseTraversalHistory = !isTicketClosed && hasBranchingPaths && displayStepOrder.length > 0;

  const renderSteps = orderedSteps.map((item) => item.step);
  const normalizedSteps = orderedSteps.map((item) => item.name);
  // Etapas já percorridas (timers + ajustes em PurchaseDetail/TicketDetail). Não usar
  // previous_steps do workflow aqui: no backend esse campo significa "origens permitidas"
  // para transição, e listar muitas etapas fazia o UI tratar todas como "ancestrais" e
  // marcar erroneamente tudo como concluído.
  const visitedSteps = new Set((shouldUseTraversalHistory ? displayStepOrder : []).map((stepName) => normalizeStepName(stepName)));

  const currentStepIndex = currentStep 
    ? normalizedSteps.findIndex((stepName: string) => normalizeStepName(stepName) === normalizeStepName(currentStep))
    : (normalizedSteps.length > 0 ? 0 : -1);

  // Calcular isLastStep localmente
  const calculatedIsLastStep = currentStepIndex >= 0 && currentStepIndex === normalizedSteps.length - 1;
  // Usar isLastStep do prop se fornecido explicitamente (prioridade), senão usar o calculado
  const isLastStep = typeof isLastStepProp === 'boolean' ? isLastStepProp : calculatedIsLastStep;
  
  // Verificar se o ticket está fechado/cancelado (normalizar para minúsculas)
  
  // Se o ticket estiver fechado, não mostrar nenhum botão de ação.
  // O backend já calcula se pode avançar, inclusive em caminhos alternativos.
  const canShowAdvanceButton = !isTicketClosed && canAdvance;
  // Mostrar botão de concluir apenas se estiver no último passo e não estiver fechado
  const canShowConcludeButton = !isTicketClosed && canConclude && isLastStep;
  // Mostrar botão de retroceder apenas se não estiver fechado
  const canShowGoBackButton = !isTicketClosed && canGoBack;

  return (
    <div className="workflow-horizontal">
      <div className="workflow-header mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-1">Fluxo de Trabalho: {workflow.name}</h6>
            {workflow.description && (
              <small className="text-muted">{workflow.description}</small>
            )}
            {!isTicketClosed && canAdvance && nextStepCount > 0 && (
              <div className="mt-1">
                <Badge bg={nextStepCount > 1 ? 'warning' : 'secondary'}>
                  {nextStepCount} caminho{nextStepCount > 1 ? 's' : ''} disponível{nextStepCount > 1 ? 'eis' : ''}
                </Badge>
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            {showExtraAction && onExtraAction && (
              <Button
                variant="outline-primary"
                size="sm"
                className="workflow-extra-action-btn"
                onClick={onExtraAction}
                disabled={isExtraActionLoading || isLoading || isGoingBack || isConcluding}
              >
                {isExtraActionLoading ? 'Processando...' : extraActionLabel}
              </Button>
            )}
            {canShowGoBackButton && onGoBack && (
              <Button 
                variant="warning"
                size="sm"
                className="workflow-nav-btn workflow-nav-btn-back"
                onClick={onGoBack}
                disabled={isGoingBack || isLoading || isConcluding}
              >
                {isGoingBack ? 'Retrocedendo...' : 'Retroceder passo'}
              </Button>
            )}
            {canShowAdvanceButton && onAdvance && (
              <Button 
                variant="success"
                size="sm"
                className="workflow-nav-btn workflow-nav-btn-next"
                onClick={onAdvance}
                disabled={isLoading || isGoingBack || isConcluding}
              >
                {isLoading ? 'Avançando...' : 'Avançar passo'}
              </Button>
            )}
            {canShowConcludeButton && onConclude && (
              <Button 
                variant="danger"
                size="sm"
                onClick={onConclude}
                disabled={isConcluding || isLoading || isGoingBack}
              >
                {isConcluding ? 'Concluindo...' : 'Concluir'}
              </Button>
            )}
          </div>
        </div>
      </div>
      
      <div className="workflow-steps-horizontal">
        {renderSteps.map((step, index) => {
          const stepName = getStepName(step);
          // Verificar se o ticket está fechado (normalizar para minúsculas)
          const isTicketClosed = ticketStatus?.toLowerCase() === 'fechado';
          // Verificar se este é o último índice do array de steps
          const isLastIndex = index === renderSteps.length - 1;
          // Verificar se este é o passo atual
          const isCurrentStep = currentStepIndex === index;
          const hasVisitedOrder = shouldUseTraversalHistory;
          const isVisited = visitedSteps.has(normalizeStepName(stepName));
          // Com histórico: só marcar concluído o que realmente entrou em displayStepOrder
          // (e não o passo atual). Sem histórico: fallback pela ordem linear no JSON.
          const isCompleted = hasVisitedOrder
            ? (isVisited && !isCurrentStep) || (isLastIndex && isTicketClosed)
            : currentStepIndex > index || (isLastIndex && isTicketClosed);
          // Marcar como atual apenas se não estiver concluído, não estiver fechado e for o passo atual
          const isCurrent = !isCompleted && !isTicketClosed && (isCurrentStep || (index === 0 && currentStepIndex === -1));
          const isPending = !isCurrent && !isCompleted;

          return (
            <React.Fragment key={index}>
              <div className="workflow-step-item">
                <div className={`step-indicator ${isCurrent ? 'current' : isCompleted ? 'completed' : 'pending'}`}>
                  {isCompleted ? (
                    <span className="step-check">✓</span>
                  ) : (
                    <span className="step-number">{index + 1}</span>
                  )}
                </div>
                <div className="step-content">
                  <div className="step-name">{stepName}</div>
                  {isCurrent && <Badge bg="primary" className="mt-1">Atual</Badge>}
                  {isCompleted && <Badge bg="success" className="mt-1">Concluído</Badge>}
                  {isPending && <Badge bg="secondary" className="mt-1">Pendente</Badge>}
                </div>
              </div>
              {index < renderSteps.length - 1 && (
                <div className={`step-arrow ${isCompleted ? 'completed' : ''}`}>
                  →
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowVisualizerHorizontal;


